"use client";

import { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import { motion, AnimatePresence, useInView } from "framer-motion";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  Users,
  BookOpen,
  Music,
  ChevronRight,
  X,
  Calendar,
  MapPin,
  ArrowLeft,
  ArrowRight,
  Play,
  Clock,
  ChevronDown,
} from "lucide-react";
import Navbar from "../Header/Navbar";
import Footer from "../Header/Footer";

// Enhanced image collection with proper dimensions
const images = {
  hero: {
    // src: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80",
    src: "/media/campus-life/reception.jpg",
    width: 1920,
    height: 1080,
    alt: "NASTP Campus Main Building",
  },
  studentLife: {
    src: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    width: 800,
    height: 600,
    alt: "Students collaborating in a study group",
  },
  academics: {
    src: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    width: 800,
    height: 600,
    alt: "Modern library with students studying",
  },
  sports: {
    src: "https://images.unsplash.com/photo-1517649763962-0c623066013b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    width: 800,
    height: 600,
    alt: "Students playing basketball in the campus gym",
  },
  arts: {
    src: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    width: 800,
    height: 600,
    alt: "Art exhibition in the campus gallery",
  },
  video: {
    src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    width: 1200,
    height: 800,
    alt: "Students collaborating on a project",
  },
};

// Gallery images with proper dimensions and optimized sizes
const galleryImages = [
  {
    id: 1,
    src: "images/campus/gallery/gaming-garag.png",
    width: 600,
    height: 400,
    alt: "An exciting moment at the Gaming Garage — where tech meets innovation.",
    category: "gaming garage",
  },
  {
    id: 2,
    src: "images/campus/gallery/hp-gaming2.png",
    width: 600,
    height: 400,
    alt: "Building connections and forging partnerships — a moment of warm greetings and professional camaraderie at the Gaming Garage event.",
    category: "gaming garage",
  },
  {
    id: 3,
    src: "images/campus/gallery/hp-gaming-garag1.png",
    width: 600,
    height: 400,
    alt: "Cutting the ribbon to new beginnings — an exciting milestone at the Gaming Garage launch, marking the start of a thrilling journey in tech and gaming innovation.",
    category: "gaming garage",
  },
  {
    id: 4,
    src: "images/campus/gallery/womens-day.png",
    width: 600,
    height: 400,
    alt: "Cutting the cake in celebration of unity and empowerment!",
    category: "women's day",
  },
  {
    id: 5,
    src: "images/campus/gallery/womens-day-2.png",
    width: 600,
    height: 400,
    alt: "Sharing smiles and celebrating togetherness on this special occasion!",
    category: "women's day",
  },
  {
    id: 6,
    src: "images/campus/gallery/womens-day-auditorium.png",
    width: 600,
    height: 400,
    alt: "Empowered voices and vibrant energy filling the auditorium as women come together to celebrate International Women's Day!",
    category: "women's day",
  },
  {
    id: 7,
    src: "images/campus/gallery/Marathon1.png",
    width: 600,
    height: 400,
    alt: "Team spirit and determination on full display! Ready, set, go — our participants are geared up and racing towards victory",
    category: "marathon race",
  },
  {
    id: 8,
    src: "images/campus/gallery/marathon.JPG",
    width: 600,
    height: 400,
    alt: "Runners pushing their limits, fueled by the cheers of the crowd — the race is on.",
    category: "marathon race",
  },
];

// Campus stats
const campusStats = [
  // { label: "Students", value: "300+", icon: Users },
  // { label: "Courses", value: "4+", icon: BookOpen },
  // { label: "Acres", value: "120", icon: Globe },
  // { label: "Awards", value: "50+", icon: Award },
];

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Campus life aspects with gallery images for each category
const campusLifeAspects = [
  {
    title: "Marathon Race",
    description:
      "Challenge yourself and celebrate endurance in our annual marathon where students, faculty, and alumni run together for fitness, fun, and a shared spirit of determination.",
    icon: Users,
    link: "#",
    image: images.studentLife,
    src: "images/campus/Marathon1.png",

    alt: "Student Club Fair",
  },
  {
    title: "Sports Week",
    description:
      "Experience the thrill of competition and teamwork during Sports Week a vibrant celebration of athleticism, spirit, and university pride across a variety of games and events.",
    icon: BookOpen,
    link: "#",
    image: images.academics,
    src: "images/campus/sports-week-match.png",
    alt: "Modern Library",
  },
  {
    title: "Women's Day",
    description:
      "Celebrate the strength, achievements, and voices of women through inspiring events, discussions, and activities that promote equality, empowerment, and inclusion on campus.",
    icon: Music,
    link: "#",
    image: images.arts,
    src: "images/campus/womens-day-2.png",
    alt: "Art Exhibition",
  },
];

const CampusLifeCard = ({
  title,
  description,
  icon: Icon,
  link,
  image,
  gallery,
  onLearnMoreClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeIn}
      className="relative overflow-hidden rounded-xl shadow-lg"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
    >
      <img
        src={typeof image === "object" ? image.src : image}
        alt={typeof image === "object" ? image.alt : title}
        width={typeof image === "object" ? image.width : 800}
        height={typeof image === "object" ? image.height : 600}
        className="w-full h-64 object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70" />
      <div className="absolute bottom-0 left-0 p-6 text-white">
        <Icon className="w-8 h-8 mb-2" />
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <AnimatePresence>
          {isHovered && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              className="text-sm mb-4"
            >
              {description}
            </motion.p>
          )}
        </AnimatePresence>
        <button
          onClick={() => onLearnMoreClick({ title, description, gallery })}
          className="inline-flex items-center text-blue-300 hover:text-blue-100 transition-colors duration-300"
        >
          Learn More <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </motion.div>
  );
};

const EventModal = ({ event, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9, y: 50 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.9, y: 50 }}
      className="bg-white rounded-lg p-8 max-w-md w-full m-4 relative"
      onClick={(e) => e.stopPropagation()}
    >
      {event.image && (
        <div className="mb-6 -mx-8 -mt-8">
          <img
            src={event.image || "/placeholder.svg"}
            alt={event.event}
            className="w-full h-48 object-cover rounded-t-lg"
          />
        </div>
      )}
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-2xl font-bold">{event.event}</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X size={24} />
        </button>
      </div>
      <p className="text-gray-600 mb-4">{event.description}</p>
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="text-blue-600" size={18} />
        <span className="text-blue-800 font-semibold">{event.date}</span>
      </div>
      {event.location && (
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="text-blue-600" size={18} />
          <span className="text-gray-700">{event.location}</span>
        </div>
      )}
      {event.time && (
        <div className="flex items-center gap-2 mb-4">
          <Clock className="text-blue-600" size={18} />
          <span className="text-gray-700">{event.time}</span>
        </div>
      )}
    </motion.div>
  </motion.div>
);

const PhotoGalleryModal = ({ photo, onClose, onNext, onPrev }) => {
  const [isZoomed, setIsZoomed] = useState(false);

  const toggleZoom = (e) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <X size={32} />
        </button>

        <div className="flex items-center justify-between w-full">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-70"
          >
            <ArrowLeft size={24} />
          </button>

          <div
            className={`relative w-full max-h-[80vh] overflow-hidden transition-transform duration-300 ${
              isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
            }`}
          >
            <motion.img
              src={photo.src || "/placeholder.svg"}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              className={`max-h-[60vh] w-auto mx-auto object-contain transition-transform duration-300 ${
                isZoomed ? "scale-150" : "scale-100"
              }`}
              onClick={toggleZoom}
              layoutId={`photo-${photo.id}`}
            />
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-70"
          >
            <ArrowRight size={24} />
          </button>
        </div>

        <div className="absolute bottom-8 left-0 right-0 text-center text-white">
          <h3 className="text-xl font-bold mb-2">{photo.alt}</h3>
          <p className="text-gray-300 capitalize mb-2">{photo.category}</p>
          <div className="flex justify-center gap-2">
            <span className="bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">
              {galleryImages.findIndex((img) => img.id === photo.id) + 1} of{" "}
              {galleryImages.length}
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const VideoModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.9 }}
      className="relative max-w-5xl w-full px-4"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
      >
        <X size={32} />
      </button>

      <div className="aspect-video">
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/she2tGBAX80?si=G5OGGWyU4fQhydZd"
          title="Campus Life Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    </motion.div>
  </motion.div>
);

// New component for the Campus Life Gallery Modal
const CampusLifeGalleryModal = ({ data, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % data.gallery.length);
  };

  const handlePrev = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? data.gallery.length - 1 : prev - 1
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-2xl font-bold">{data.title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <p className="text-gray-600 mb-6">{data.description}</p>

          {/* Featured image with navigation */}
          <div className="relative mb-6 aspect-video bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={data.gallery[currentImageIndex].src || "/placeholder.svg"}
              alt={data.gallery[currentImageIndex].alt}
              className="w-full h-full object-cover"
            />

            <button
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-70"
            >
              <ArrowLeft size={24} />
            </button>

            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-70"
            >
              <ArrowRight size={24} />
            </button>

            <div className="absolute bottom-2 left-0 right-0 text-center">
              <p className="text-white text-sm bg-black bg-opacity-50 inline-block px-3 py-1 rounded-full">
                {currentImageIndex + 1} / {data.gallery.length}
              </p>
            </div>
          </div>

          {/* Gallery grid */}
          <div className="grid grid-cols-3 gap-2">
            {data.gallery.map((image, index) => (
              <div
                key={index}
                className={`aspect-square rounded-lg overflow-hidden cursor-pointer ${
                  currentImageIndex === index ? "ring-2 ring-blue-600" : ""
                }`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img
                  src={image.src || "/placeholder.svg"}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CampusLife = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredImages, setFilteredImages] = useState(galleryImages);
  const [visibleImages, setVisibleImages] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedCampusLife, setSelectedCampusLife] = useState(null);
  const [visibleRows, setVisibleRows] = useState(3);
  const imagesPerRow = 4; // Assuming 4 images per row on desktop

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Update filtered images when category changes
  const [originalFilteredImages, setOriginalFilteredImages] =
    useState(galleryImages);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredImages(galleryImages);
      setOriginalFilteredImages(galleryImages);
    } else {
      const filtered = galleryImages.filter(
        (img) => img.category === activeCategory
      );
      setFilteredImages(filtered);
      setOriginalFilteredImages(filtered);
    }
    // Reset to showing only 3 rows when category changes
    setVisibleRows(3);
  }, [activeCategory]);

  useEffect(() => {
    setVisibleImages(filteredImages.slice(0, visibleRows * imagesPerRow));
  }, [filteredImages, visibleRows, imagesPerRow]);

  const handleShowMore = () => {
    setVisibleRows((prevRows) => {
      const newRows = prevRows + 1;
      const newVisibleImages = originalFilteredImages.slice(
        0,
        newRows * imagesPerRow
      );
      setVisibleImages(newVisibleImages);
      return newRows;
    });
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === "all") {
      setFilteredImages(galleryImages);
      setOriginalFilteredImages(galleryImages);
    } else {
      const filtered = galleryImages.filter((img) => img.category === category);
      setFilteredImages(filtered);
      setOriginalFilteredImages(filtered);
    }
    setVisibleRows(3);
  };

  const handleNextPhoto = () => {
    const currentIndex = galleryImages.findIndex(
      (img) => img.id === selectedPhoto.id
    );
    const nextIndex = (currentIndex + 1) % galleryImages.length;
    setSelectedPhoto(galleryImages[nextIndex]);
  };

  const handlePrevPhoto = () => {
    const currentIndex = galleryImages.findIndex(
      (img) => img.id === selectedPhoto.id
    );
    const prevIndex =
      (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedPhoto(galleryImages[prevIndex]);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
  };

  const events = [
    {
      date: "May 15",
      event: "Spring Music Festival",
      description:
        "Annual celebration of music featuring student performances and guest artists from around the world.",
      location: "Main Auditorium",
      time: "6:00 PM - 10:00 PM",
      image:
        "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    },
    {
      date: "June 1",
      event: "Innovation Expo",
      description:
        "Showcase your groundbreaking ideas and connect with industry leaders at our annual innovation fair.",
      location: "Technology Center",
      time: "10:00 AM - 4:00 PM",
      image:
        "https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    },
    {
      date: "July 10",
      event: "Global Culture Week",
      description:
        "Experience a week-long celebration of diversity with international cuisines, performances, and cultural exchanges.",
      location: "Student Union",
      time: "All Day",
      image:
        "https://images.unsplash.com/photo-1511988617509-a57c8a288659?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    },
    {
      date: "August 20",
      event: "Freshman Welcome Bash",
      description:
        "Kick off your college journey with an unforgettable welcome party for all new students.",
      location: "Campus Green",
      time: "5:00 PM - 11:00 PM",
      image:
        "https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const categories = [
    { id: "all", label: "All Photos" },
    { id: "gaming garage", label: "Gaming Garage" },
    { id: "women's day", label: "women's day" },
    { id: "marathon race", label: "Marathon Race" },
  ];

  const handleCampusLifeLearnMore = (data) => {
    setSelectedCampusLife(data);
  };

  // Image loading with progressive enhancement
  const ImageWithLoading = ({ image }) => {
    const [loaded, setLoaded] = useState(false);

    return (
      <div className="aspect-square bg-gray-200 relative overflow-hidden rounded-lg">
        {/* High resolution image that loads after */}
        <img
          src={image.src || "/placeholder.svg"}
          alt={image.alt}
          width={image.width}
          height={image.height}
          className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setLoaded(true)}
        />
      </div>
    );
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <Navbar />
      {/* Loading animation */}
      <AnimatePresence>
        {!isLoaded && (
          <motion.div
            className="fixed inset-0 bg-blue-900 z-50 flex items-center justify-center"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 2,
              }}
            >
              <BookOpen size={64} className="text-white" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="relative h-screen">
        <img
          src={images.hero.src || "/placeholder.svg"}
          alt={images.hero.alt}
          width={images.hero.width}
          height={images.hero.height}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-purple-900 opacity-70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-white"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Experience NIIT
            </motion.h1>
            <motion.p
              className="text-xl sm:text-2xl text-blue-200 mb-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Where Innovation Meets Inspiration
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.button
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-xl font-semibold hover:bg-white hover:bg-opacity-10 transition-colors duration-300 shadow-lg flex items-center justify-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowVideo(true)}
              >
                <Play size={20} />
                Watch Video
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Campus Stats */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container mx-auto px-4">
            <div className="bg-white bg-opacity-95 rounded-t-xl shadow-xl grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
              {campusStats.map((stat, index) => (
                <div key={index} className="p-6 text-center">
                  <stat.icon className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-3xl font-bold text-gray-800">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        {/* Photo Gallery Section */}
        <section className="mb-24">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-800">
              Campus Photo Gallery
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our beautiful campus through these stunning photographs
              capturing student life, academic excellence, and the vibrant
              community that makes NASTP special.
            </p>
          </motion.div>

          {/* Gallery Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-8 sticky top-0 bg-white bg-opacity-90 py-4 z-10 rounded-lg shadow-md">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            key={activeCategory} // Force re-render when category changes
          >
            {visibleImages.map((image) => (
              <motion.div
                key={image.id}
                variants={fadeIn}
                className="relative overflow-hidden rounded-lg cursor-pointer"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.2 }}
                onClick={() => setSelectedPhoto(image)}
              >
                <ImageWithLoading image={image} />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-0 hover:opacity-80 transition-opacity duration-300 flex items-end p-4">
                  <div>
                    <h3 className="text-white font-bold">{image.alt}</h3>
                    <p className="text-gray-300 text-sm capitalize">
                      {image.category}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Show More Button - only display if there are more images to show */}
          {visibleImages.length < originalFilteredImages.length && (
            <div className="text-center mt-8">
              <button
                onClick={handleShowMore}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors duration-300 inline-flex items-center gap-2 shadow-lg"
              >
                <ChevronDown size={18} />
                Show More Photos ({visibleImages.length} of{" "}
                {originalFilteredImages.length})
              </button>
            </div>
          )}
        </section>

        <section className="mb-24 bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="p-8">
            <motion.h2
              className="text-4xl font-bold mb-8 text-center text-gray-800"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
            >
              Campus Highlights
            </motion.h2>
            <Slider {...sliderSettings}>
              {campusLifeAspects.map((aspect, index) => (
                <div
                  key={index}
                  className="px-4"
                  style={{ width: "100%", height: "600px" }}
                >
                  <img
                    src={aspect.src || "/placeholder.svg"}
                    alt={aspect.alt}
                    width="100%"
                    height="100%"
                    className="w-full h-96 object-cover rounded-lg"
                  />
                  <h3 className="text-2xl font-bold mt-6 mb-2">
                    {aspect.title}
                  </h3>
                  <p className="text-gray-600">{aspect.description}</p>
                </div>
              ))}
            </Slider>
          </div>
        </section>

        {/* <section className="mb-24">
          <motion.h2
            className="text-4xl font-bold mb-12 text-center text-gray-800"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            Upcoming Events
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {events.map((event, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="bg-white p-6 rounded-xl shadow-lg cursor-pointer overflow-hidden"
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                }}
                transition={{ duration: 0.2 }}
                onClick={() => setSelectedEvent(event)}
              >
                {event.image && (
                  <div className="h-40 -mx-6 -mt-6 mb-4">
                    <img
                      src={event.image || "/placeholder.svg"}
                      alt={event.event}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="bg-blue-100 text-blue-800 font-semibold px-3 py-1 rounded-full text-sm inline-block mb-4">
                  {event.date}
                </div>
                <h3 className="text-xl font-bold mb-2">{event.event}</h3>
                <p className="text-gray-600 line-clamp-2 mb-3">
                  {event.description}
                </p>
                <div className="flex flex-col gap-2">
                  {event.location && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <MapPin size={16} />
                      <span className="text-sm">{event.location}</span>
                    </div>
                  )}
                  {event.time && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock size={16} />
                      <span className="text-sm">{event.time}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section> */}
      </main>

      <AnimatePresence>
        {selectedEvent && (
          <EventModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
        )}

        {selectedPhoto && (
          <PhotoGalleryModal
            photo={selectedPhoto}
            onClose={() => setSelectedPhoto(null)}
            onNext={handleNextPhoto}
            onPrev={handlePrevPhoto}
          />
        )}

        {selectedCampusLife && (
          <CampusLifeGalleryModal
            data={selectedCampusLife}
            onClose={() => setSelectedCampusLife(null)}
          />
        )}

        {showVideo && <VideoModal onClose={() => setShowVideo(false)} />}
      </AnimatePresence>

      <Footer />
    </div>
  );
};
export default CampusLife;
